import { useCallback, useEffect, useState } from 'react';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { getDeviceStatusCode } from 'utils/devices/icon.utils';

export default function useMaintenanceGroups() {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
  } = useGetGroupsQuery();

  const [groups, setGroups] = useState([]);
  const fetching = connectedDevicesLoading || deviceGroupsLoading;

  const generateGroup = useCallback(
    (initialGroup) => {
      if (!initialGroup?.devices?.length) return initialGroup;

      const groupDevices = (initialGroup.devices ?? [])
        .map((deviceId) => {
          const device = connectedDevices?.find((d) => d.id === deviceId);
          if (!device) return null;

          return {
            id: device.id,
            alias: device.alias,
            name: device.name,
            device_type: device.device_type,
            status: getDeviceStatusCode(device),
            acPowerFail: device.data?.[0]?.ACPowerFail ?? null,
            batteryFail: device.data?.[0]?.BatteryFail ?? null,
          };
        })
        .filter(Boolean);

      const status = groupDevices.reduce(
        (acc, value) => (acc.includes(value.status) ? acc : [...acc, value.status]),
        [],
      );

      const acPowerFail = groupDevices.some((d) => d.acPowerFail === 'Fail') ? 'Fail' : 'Normal';
      const batteryFail = groupDevices.some((d) => d.batteryFail === 'Fail') ? 'Fail' : 'Normal';

      return {
        id: initialGroup.id,
        name: initialGroup.name,
        type: initialGroup.type,
        devices: groupDevices,
        deviceCount: groupDevices.length,
        status,
        acPowerFail,
        batteryFail,
      };
    },
    [connectedDevices],
  );

  const getMaintenanceGroups = useCallback(() => {
    if (!deviceGroups.length) return [];

    let newGroups = [];
    let foundDevices = [];

    deviceGroups
      .filter((group) => group.id !== 36)
      .forEach((group) => {
        newGroups.push(generateGroup(group));

        (group.devices ?? []).forEach((deviceId) => {
          if (!foundDevices.includes(deviceId)) foundDevices.push(deviceId);
        });
      });

    const missedDevices = connectedDevices
      .filter((device) => !foundDevices.includes(device.id))
      .map((device) => device.id);

    if (missedDevices.length > 0) {
      const defaultGroup = {
        id: '',
        name: 'Misc. Devices',
        type: '',
        devices: missedDevices,
      };

      newGroups.push(generateGroup(defaultGroup));
    }

    return newGroups;
  }, [connectedDevices, deviceGroups, generateGroup]);

  useEffect(() => {
    const nextGroups = fetching ? { fetching: true } : getMaintenanceGroups();

    setGroups((prev) => {
      try {
        if (JSON.stringify(prev) === JSON.stringify(nextGroups)) return prev;
      } catch (error) {
        console.error('Error comparing maintenance groups for state update:', error);
      }

      return nextGroups;
    });
  }, [fetching, getMaintenanceGroups]);

  return groups;
}
